import { useState } from 'react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import {
    CalendarIcon,
    ChevronDown,
    ChevronRight,
    History,
    Loader2,
    MinusCircle,
    Plus,
    PlusCircle,
    Pencil,
    Search,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '~/lib/utils';
import { trpc } from '~/api/trpc';
import Content from '~/components/layout/Content';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';

type GiftCardRow = {
    id: string;
    giftCardNumber: string;
    name: string;
    type: string;
    initialValueEur: number;
    remainingBalance: number;
    usedAmount: number;
    buyerCustomer: { id: number; firstName: string; lastName: string; email: string | null } | null;
    recipientCustomer: { id: number; firstName: string; lastName: string; email: string | null } | null;
    invoiceId: string;
    invoiceNumber: string;
    sourceBillingDraftId: string | null;
    sourceBillingDraftNumber: string | null;
    createdAt: string;
    deletedAt: string | null;
    transactionCount: number;
};

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
    }).format(amount);
};

// Expanded row component for transaction history + adjustment
const ExpandedRowContent = ({
    giftCard,
    isAdmin,
}: {
    giftCard: GiftCardRow;
    isAdmin: boolean;
}) => {
    const navigate = useNavigate();
    const [showAdjustmentForm, setShowAdjustmentForm] = useState(false);
    const [adjustmentAmount, setAdjustmentAmount] = useState<string>('');
    const [adjustmentReason, setAdjustmentReason] = useState('');
    const trpcUtils = trpc.useUtils();

    const { data: transactions, isLoading } = trpc.giftCards.getTransactions.useQuery(
        { giftCardId: giftCard.id }
    );

    const adjustMutation = trpc.giftCards.adjust.useMutation({
        onSuccess: () => {
            toast.success('Gutscheinkarte wurde angepasst');
            trpcUtils.giftCards.getAll.invalidate();
            trpcUtils.giftCards.getTransactions.invalidate({ giftCardId: giftCard.id });
            setShowAdjustmentForm(false);
            setAdjustmentAmount('');
            setAdjustmentReason('');
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const handleAdjust = () => {
        const amount = parseFloat(adjustmentAmount);
        if (!amount || amount === 0 || !adjustmentReason.trim()) {
            return;
        }
        adjustMutation.mutate({
            giftCardId: giftCard.id,
            amountEur: amount,
            reason: adjustmentReason.trim(),
        });
    };

    return (
        <div className='p-4 bg-muted/30 space-y-4'>
            {/* Transactions */}
            <div className='flex items-center gap-2'>
                <History className='h-4 w-4' />
                <span className='text-sm font-medium'>
                    Transaktionen ({giftCard.transactionCount})
                </span>
            </div>

            {isLoading ? (
                <div className='space-y-1'>
                    {[...Array(3)].map((_, i) => (
                        <Skeleton key={i} className='h-8 w-full' />
                    ))}
                </div>
            ) : transactions && transactions.length > 0 ? (
                <div className='space-y-1 max-h-40 overflow-y-auto'>
                    {transactions.map((tx) => (
                        <div
                            key={tx.id}
                            className='flex justify-between items-center text-xs py-2 px-3 bg-background rounded border'
                        >
                            <div className='flex items-center gap-2 flex-wrap'>
                                <span className={Number(tx.amountEur) > 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                                    {Number(tx.amountEur) > 0 ? '+' : ''}{formatCurrency(Number(tx.amountEur))}
                                </span>
                                <span className='text-muted-foreground'>
                                    {tx.type === 'REDEMPTION' && 'Eingelöst'}
                                    {tx.type === 'REFUND' && 'Erstattet'}
                                    {tx.type === 'ADJUSTMENT' && 'Korrektur'}
                                </span>
                                {tx.payment?.billingDraft && (
                                    <span
                                        className='font-mono text-primary cursor-pointer hover:underline'
                                        onClick={() => navigate(`/billing-draft/${tx.payment!.billingDraft!.id}`)}
                                    >
                                        Entwurf {tx.payment.billingDraft.billingDraftNumber}
                                    </span>
                                )}
                                {tx.description && (
                                    <span className='text-muted-foreground'>- {tx.description}</span>
                                )}
                            </div>
                            <span className='text-muted-foreground shrink-0 ml-2'>
                                {format(new Date(tx.createdAt), 'dd.MM.yyyy HH:mm', { locale: de })}
                            </span>
                        </div>
                    ))}
                </div>
            ) : (
                <p className='text-sm text-muted-foreground'>
                    Noch keine Transaktionen
                </p>
            )}

            {/* Admin adjustment section */}
            {/*
            {isAdmin && !giftCard.deletedAt && (
                <>
                    <Separator />
                    {!showAdjustmentForm ? (
                        <Button
                            variant='outline'
                            size='sm'
                            onClick={() => setShowAdjustmentForm(true)}
                        >
                            Manuell anpassen
                        </Button>
                    ) : (
                        <div className='space-y-3 p-3 border rounded-lg bg-background'>
                            <div className='space-y-2'>
                                <Label>Betrag hinzufügen/entfernen (€)</Label>
                                <div className='flex items-center gap-2'>
                                    <Button
                                        type='button'
                                        variant='outline'
                                        size='icon'
                                        onClick={() => {
                                            const current = parseFloat(adjustmentAmount) || 0;
                                            setAdjustmentAmount((current - 1).toFixed(2));
                                        }}
                                    >
                                        <MinusCircle className='h-4 w-4' />
                                    </Button>
                                    <Input
                                        type='number'
                                        step='0.01'
                                        value={adjustmentAmount}
                                        onChange={(e) => setAdjustmentAmount(e.target.value)}
                                        placeholder='0.00'
                                        className='w-28 text-center'
                                    />
                                    <Button
                                        type='button'
                                        variant='outline'
                                        size='icon'
                                        onClick={() => {
                                            const current = parseFloat(adjustmentAmount) || 0;
                                            setAdjustmentAmount((current + 1).toFixed(2));
                                        }}
                                    >
                                        <PlusCircle className='h-4 w-4' />
                                    </Button>
                                </div>
                                <p className='text-xs text-muted-foreground'>
                                    Positiv = Guthaben aufladen, Negativ = Guthaben abziehen
                                </p>
                            </div>
                            <div className='space-y-2'>
                                <Label>Begründung *</Label>
                                <Textarea
                                    value={adjustmentReason}
                                    onChange={(e) => setAdjustmentReason(e.target.value)}
                                    placeholder='Grund für die Anpassung...'
                                    rows={2}
                                />
                            </div>
                            <div className='flex gap-2'>
                                <Button
                                    variant='outline'
                                    size='sm'
                                    onClick={() => {
                                        setShowAdjustmentForm(false);
                                        setAdjustmentAmount('');
                                        setAdjustmentReason('');
                                    }}
                                >
                                    Abbrechen
                                </Button>
                                <Button
                                    size='sm'
                                    onClick={handleAdjust}
                                    disabled={!adjustmentAmount || parseFloat(adjustmentAmount) === 0 || !adjustmentReason.trim() || adjustMutation.isPending}
                                >
                                    {adjustMutation.isPending && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                                    Anpassen
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
            */}
        </div>
    );
};

// Create gift card dialog — requires selecting a template first
const CreateGiftCardDialog = ({
    open,
    onOpenChange,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}) => {
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
    const [invoiceSearch, setInvoiceSearch] = useState('');
    const [selectedInvoiceId, setSelectedInvoiceId] = useState('');
    const [selectedInvoiceNumber, setSelectedInvoiceNumber] = useState('');
    const [buyerSearch, setBuyerSearch] = useState('');
    const [selectedBuyerId, setSelectedBuyerId] = useState<number | null>(null);
    const [selectedBuyerName, setSelectedBuyerName] = useState('');
    const [recipientSearch, setRecipientSearch] = useState('');
    const [selectedRecipientId, setSelectedRecipientId] = useState<number | null>(null);
    const [selectedRecipientName, setSelectedRecipientName] = useState('');

    const trpcUtils = trpc.useUtils();

    const { data: templatesData } = trpc.giftCardTemplates.getAll.useQuery(undefined, {
        enabled: open,
    });

    const { data: invoiceData } = trpc.giftCards.searchInvoices.useQuery(
        { search: invoiceSearch, limit: 10 },
        { enabled: invoiceSearch.length >= 2 }
    );

    const { data: buyerData } = trpc.giftCards.searchCustomers.useQuery(
        { search: buyerSearch, limit: 10 },
        { enabled: buyerSearch.length >= 2 }
    );

    const { data: recipientData } = trpc.giftCards.searchCustomers.useQuery(
        { search: recipientSearch, limit: 10 },
        { enabled: recipientSearch.length >= 2 }
    );

    const createMutation = trpc.giftCards.create.useMutation({
        onSuccess: (data) => {
            toast.success(`Gutscheinkarte ${data.data.giftCardNumber} erstellt`);
            trpcUtils.giftCards.getAll.invalidate();
            onOpenChange(false);
            resetForm();
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const templates = templatesData?.data ?? [];
    const selectedTemplate = templates.find((t) => t.id === selectedTemplateId) ?? null;

    const resetForm = () => {
        setSelectedTemplateId(null);
        setInvoiceSearch('');
        setSelectedInvoiceId('');
        setSelectedInvoiceNumber('');
        setBuyerSearch('');
        setSelectedBuyerId(null);
        setSelectedBuyerName('');
        setRecipientSearch('');
        setSelectedRecipientId(null);
        setSelectedRecipientName('');
    };

    const handleCreate = () => {
        if (!selectedTemplateId) {
            toast.error('Bitte wählen Sie eine Vorlage');
            return;
        }
        if (!selectedInvoiceId) {
            toast.error('Bitte wählen Sie eine Rechnung');
            return;
        }

        createMutation.mutate({
            giftCardTemplateId: selectedTemplateId,
            invoiceId: selectedInvoiceId,
            buyerCustomerId: selectedBuyerId,
            recipientCustomerId: selectedRecipientId,
        });
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { resetForm(); } }}>
            <DialogContent className='max-w-lg max-h-[85vh] overflow-hidden flex flex-col'>
                <DialogHeader>
                    <DialogTitle>Neue Gutscheinkarte erstellen</DialogTitle>
                    <DialogDescription>
                        Wählen Sie eine Vorlage und verknüpfen Sie die Gutscheinkarte mit einer Rechnung.
                    </DialogDescription>
                </DialogHeader>
                <div className='flex-1 overflow-auto space-y-4'>
                    {/* Template selection */}
                    <div className='space-y-2'>
                        <Label>Vorlage *</Label>
                        {templates.length === 0 ? (
                            <p className='text-sm text-muted-foreground'>
                                Keine Vorlagen vorhanden. Bitte erstellen Sie zuerst eine Vorlage unter Gutscheinkarten-Vorlagen.
                            </p>
                        ) : (
                            <Select
                                value={selectedTemplateId ?? ''}
                                onValueChange={(v) => setSelectedTemplateId(v)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder='Vorlage auswählen...' />
                                </SelectTrigger>
                                <SelectContent>
                                    {templates.map((t) => (
                                        <SelectItem key={t.id} value={t.id}>
                                            {t.name} — {formatCurrency(Number(t.valueAmount))}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    </div>

                    {/* Show selected template details (read-only) */}
                    {selectedTemplate && (
                        <div className='p-3 border rounded-lg bg-muted/30 space-y-1'>
                            <div className='flex justify-between text-sm'>
                                <span className='text-muted-foreground'>Name:</span>
                                <span className='font-medium'>{selectedTemplate.name}</span>
                            </div>
                            <div className='flex justify-between text-sm'>
                                <span className='text-muted-foreground'>Wert:</span>
                                <span className='font-medium'>{formatCurrency(Number(selectedTemplate.valueAmount))}</span>
                            </div>
                            <div className='flex justify-between text-sm'>
                                <span className='text-muted-foreground'>Typ:</span>
                                <span className='font-medium'>
                                    {selectedTemplate.type === 'USE_EVERYWHERE' ? 'Überall einsetzbar' : 'Eingeschränkt'}
                                </span>
                            </div>
                            {selectedTemplate.type === 'USE_LIMITED_TREATMENT_VARIANTS' && selectedTemplate.validTreatmentVariants.length > 0 && (
                                <div className='pt-1'>
                                    <span className='text-xs text-muted-foreground'>Gültig für:</span>
                                    <div className='flex flex-wrap gap-1 mt-1'>
                                        {selectedTemplate.validTreatmentVariants.map((v) => (
                                            <Badge key={v.treatmentVariant.id} variant='secondary' className='text-xs'>
                                                {v.treatmentVariant.name}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Invoice search (required) */}
                    <div className='space-y-2'>
                        <Label>Rechnung *</Label>
                        {selectedInvoiceId ? (
                            <div className='flex items-center gap-2 p-2 border rounded bg-muted/30'>
                                <span className='font-mono text-sm'>{selectedInvoiceNumber}</span>
                                <Button
                                    variant='ghost'
                                    size='sm'
                                    className='ml-auto h-6 px-2'
                                    onClick={() => {
                                        setSelectedInvoiceId('');
                                        setSelectedInvoiceNumber('');
                                        setInvoiceSearch('');
                                    }}
                                >
                                    Ändern
                                </Button>
                            </div>
                        ) : (
                            <>
                                <div className='relative'>
                                    <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                                    <Input
                                        placeholder='Rechnungsnummer suchen...'
                                        value={invoiceSearch}
                                        onChange={(e) => setInvoiceSearch(e.target.value)}
                                        className='pl-9'
                                    />
                                </div>
                                {invoiceSearch.length >= 2 && invoiceData && (
                                    <div className='max-h-32 overflow-y-auto border rounded bg-white'>
                                        {invoiceData.data.length === 0 ? (
                                            <p className='text-sm text-muted-foreground py-3 text-center'>Keine Rechnungen gefunden</p>
                                        ) : (
                                            invoiceData.data.map((inv) => (
                                                <div
                                                    key={inv.id}
                                                    className='p-2 hover:bg-gray-100 cursor-pointer border-b last:border-b-0'
                                                    onClick={() => {
                                                        setSelectedInvoiceId(inv.id);
                                                        setSelectedInvoiceNumber(inv.invoiceNumber);
                                                        setInvoiceSearch('');
                                                    }}
                                                >
                                                    <p className='text-sm font-mono font-medium'>{inv.invoiceNumber}</p>
                                                    <p className='text-xs text-muted-foreground'>
                                                        {inv.billingDraft?.customer
                                                            ? `${inv.billingDraft.customer.firstName} ${inv.billingDraft.customer.lastName}`
                                                            : 'Kein Kunde'
                                                        }
                                                        {' · '}
                                                        {format(new Date(inv.createdAt), 'dd.MM.yyyy', { locale: de })}
                                                    </p>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Buyer customer search (optional) */}
                    <div className='space-y-2'>
                        <Label>Käufer (optional)</Label>
                        {selectedBuyerId ? (
                            <div className='flex items-center gap-2 p-2 border rounded bg-muted/30'>
                                <span className='text-sm'>{selectedBuyerName}</span>
                                <Button
                                    variant='ghost'
                                    size='sm'
                                    className='ml-auto h-6 px-2'
                                    onClick={() => {
                                        setSelectedBuyerId(null);
                                        setSelectedBuyerName('');
                                        setBuyerSearch('');
                                    }}
                                >
                                    Entfernen
                                </Button>
                            </div>
                        ) : (
                            <>
                                <div className='relative'>
                                    <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                                    <Input
                                        placeholder='Kunde suchen...'
                                        value={buyerSearch}
                                        onChange={(e) => setBuyerSearch(e.target.value)}
                                        className='pl-9'
                                    />
                                </div>
                                {buyerSearch.length >= 2 && buyerData && (
                                    <div className='max-h-28 overflow-y-auto border rounded bg-white'>
                                        {buyerData.data.length === 0 ? (
                                            <p className='text-sm text-muted-foreground py-3 text-center'>Kein Kunde gefunden</p>
                                        ) : (
                                            buyerData.data.map((c) => (
                                                <div
                                                    key={c.id}
                                                    className='p-2 hover:bg-gray-100 cursor-pointer border-b last:border-b-0'
                                                    onClick={() => {
                                                        setSelectedBuyerId(c.id);
                                                        setSelectedBuyerName(`${c.firstName} ${c.lastName}`);
                                                        setBuyerSearch('');
                                                    }}
                                                >
                                                    <p className='text-sm font-medium'>{c.firstName} {c.lastName}</p>
                                                    {c.email && <p className='text-xs text-muted-foreground'>{c.email}</p>}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Recipient customer search (optional) */}
                    <div className='space-y-2'>
                        <Label>Empfänger (optional)</Label>
                        {selectedRecipientId ? (
                            <div className='flex items-center gap-2 p-2 border rounded bg-muted/30'>
                                <span className='text-sm'>{selectedRecipientName}</span>
                                <Button
                                    variant='ghost'
                                    size='sm'
                                    className='ml-auto h-6 px-2'
                                    onClick={() => {
                                        setSelectedRecipientId(null);
                                        setSelectedRecipientName('');
                                        setRecipientSearch('');
                                    }}
                                >
                                    Entfernen
                                </Button>
                            </div>
                        ) : (
                            <>
                                <div className='relative'>
                                    <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                                    <Input
                                        placeholder='Kunde suchen...'
                                        value={recipientSearch}
                                        onChange={(e) => setRecipientSearch(e.target.value)}
                                        className='pl-9'
                                    />
                                </div>
                                {recipientSearch.length >= 2 && recipientData && (
                                    <div className='max-h-28 overflow-y-auto border rounded bg-white'>
                                        {recipientData.data.length === 0 ? (
                                            <p className='text-sm text-muted-foreground py-3 text-center'>Kein Kunde gefunden</p>
                                        ) : (
                                            recipientData.data.map((c) => (
                                                <div
                                                    key={c.id}
                                                    className='p-2 hover:bg-gray-100 cursor-pointer border-b last:border-b-0'
                                                    onClick={() => {
                                                        setSelectedRecipientId(c.id);
                                                        setSelectedRecipientName(`${c.firstName} ${c.lastName}`);
                                                        setRecipientSearch('');
                                                    }}
                                                >
                                                    <p className='text-sm font-medium'>{c.firstName} {c.lastName}</p>
                                                    {c.email && <p className='text-xs text-muted-foreground'>{c.email}</p>}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant='outline' onClick={() => { onOpenChange(false); resetForm(); }}>
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleCreate}
                        disabled={createMutation.isPending || !selectedTemplateId || !selectedInvoiceId}
                    >
                        {createMutation.isPending ? 'Erstelle...' : 'Erstellen'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const GiftCards = () => {
    const navigate = useNavigate();
    const { user } = useUserStore();
    const isAdmin = user?.role === UserRole.ADMIN || user?.role === UserRole.OPERATOR;

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'exhausted' | 'deleted'>('all');
    const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
    const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
    const [dateRangeOpen, setDateRangeOpen] = useState(false);
    const [isSelectingToDate, setIsSelectingToDate] = useState(false);
    const [page, setPage] = useState(0);
    const pageSize = 20;

    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [editTarget, setEditTarget] = useState<GiftCardRow | null>(null);
    const [editName, setEditName] = useState('');
    const [editNumber, setEditNumber] = useState('');
    const trpcUtils = trpc.useUtils();

    const updateGiftCardMutation = trpc.giftCards.update.useMutation({
        onSuccess: () => {
            toast.success('Gutscheinkarte aktualisiert');
            trpcUtils.giftCards.getAll.invalidate();
            setEditTarget(null);
        },
        onError: (error) => {
            toast.error(error.message);
        },
    });

    // Fetch gift cards
    const { data, isLoading } = trpc.giftCards.getAll.useQuery({
        page,
        pageSize,
        statusFilter,
        searchQuery: searchQuery || undefined,
        dateFrom: dateFrom ? format(dateFrom, 'yyyy-MM-dd') : undefined,
        dateTo: dateTo ? format(dateTo, 'yyyy-MM-dd') : undefined,
    });

    const columns: ColumnDef<GiftCardRow>[] = [
        {
            id: 'expander',
            header: () => null,
            cell: ({ row }) => (
                <Button
                    variant='ghost'
                    size='sm'
                    className='h-8 w-8 p-0'
                    onClick={(e) => {
                        e.stopPropagation();
                        row.toggleExpanded();
                    }}
                >
                    {row.getIsExpanded() ? (
                        <ChevronDown className='h-4 w-4' />
                    ) : (
                        <ChevronRight className='h-4 w-4' />
                    )}
                </Button>
            ),
        },
        {
            accessorKey: 'createdAt',
            header: 'Datum',
            cell: ({ row }) => (
                <span className='text-xs'>
                    {format(new Date(row.original.createdAt), 'dd.MM.yyyy', { locale: de })}
                </span>
            ),
        },
        {
            accessorKey: 'giftCardNumber',
            header: 'Nummer',
            cell: ({ row }) => (
                <span className='font-mono text-xs'>{row.original.giftCardNumber}</span>
            ),
        },
        {
            accessorKey: 'name',
            header: 'Name',
            cell: ({ row }) => (
                <span className='font-medium text-sm'>{row.original.name}</span>
            ),
        },
        {
            accessorKey: 'invoiceNumber',
            header: 'Rechnung',
            cell: ({ row }) => (
                <div
                    className='font-mono text-xs cursor-pointer hover:underline text-primary'
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/invoice/${row.original.invoiceId}`);
                    }}
                >
                    {row.original.invoiceNumber}
                </div>
            ),
        },
        {
            id: 'billingDraft',
            header: 'Entwurf',
            cell: ({ row }) => {
                if (!row.original.sourceBillingDraftId) {
                    return <span className='text-xs text-muted-foreground'>-</span>;
                }
                return (
                    <div
                        className='font-mono text-xs cursor-pointer hover:underline text-primary'
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/billing-draft/${row.original.sourceBillingDraftId}`);
                        }}
                    >
                        {row.original.sourceBillingDraftNumber}
                    </div>
                );
            },
        },
        {
            id: 'buyer',
            header: 'Käufer',
            cell: ({ row }) => {
                const buyer = row.original.buyerCustomer;
                if (!buyer) {
                    return <span className='text-xs text-muted-foreground'>-</span>;
                }
                return (
                    <div>
                        <div
                            className='cursor-pointer hover:underline font-medium text-sm'
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/customer/${buyer.id}`);
                            }}
                        >
                            {buyer.firstName} {buyer.lastName}
                        </div>
                        {buyer.email && (
                            <div className='text-xs text-muted-foreground'>{buyer.email}</div>
                        )}
                    </div>
                );
            },
        },
        {
            id: 'recipient',
            header: 'Empfänger',
            cell: ({ row }) => {
                const recipient = row.original.recipientCustomer;
                if (!recipient) {
                    return <span className='text-xs text-muted-foreground'>-</span>;
                }
                return (
                    <div>
                        <div
                            className='cursor-pointer hover:underline font-medium text-sm'
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/customer/${recipient.id}`);
                            }}
                        >
                            {recipient.firstName} {recipient.lastName}
                        </div>
                        {recipient.email && (
                            <div className='text-xs text-muted-foreground'>{recipient.email}</div>
                        )}
                    </div>
                );
            },
        },
        {
            id: 'value',
            header: 'Wert',
            cell: ({ row }) => (
                <span className='font-mono text-sm'>
                    {formatCurrency(row.original.initialValueEur)}
                </span>
            ),
        },
        {
            accessorKey: 'remainingBalance',
            header: 'Restguthaben',
            cell: ({ row }) => (
                <span className={row.original.remainingBalance > 0 ? 'text-green-600 font-medium text-sm' : 'text-muted-foreground text-sm'}>
                    {formatCurrency(row.original.remainingBalance)}
                </span>
            ),
        },
        {
            id: 'status',
            header: 'Status',
            cell: ({ row }) => {
                if (row.original.deletedAt) {
                    return (
                        <Badge variant='outline' className='bg-red-50 text-red-700 border-red-200'>
                            Gelöscht
                        </Badge>
                    );
                }
                if (row.original.usedAmount === 0 && row.original.remainingBalance === row.original.initialValueEur) {
                    return (
                        <Badge variant='outline' className='bg-blue-50 text-blue-700 border-blue-200'>
                            Neu
                        </Badge>
                    );
                }
                if (row.original.remainingBalance > 0) {
                    return (
                        <Badge variant='default' className='bg-green-100 text-green-800 hover:bg-green-100'>
                            Aktiv
                        </Badge>
                    );
                }
                return (
                    <Badge variant='outline'>
                        Aufgebraucht
                    </Badge>
                );
            },
        },
        ...(isAdmin ? [{
            id: 'actions',
            header: '',
            cell: ({ row }: { row: { original: GiftCardRow } }) => (
                <Button
                    variant='ghost'
                    size='icon'
                    className='h-7 w-7'
                    onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        setEditTarget(row.original);
                        setEditName(row.original.name);
                        setEditNumber(row.original.giftCardNumber);
                    }}
                >
                    <Pencil className='h-3.5 w-3.5' />
                </Button>
            ),
        }] : []),
    ];

    const table = useReactTable({
        data: data?.data ?? [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getRowCanExpand: () => true,
        manualPagination: true,
        pageCount: data?.pageCount ?? 0,
    });

    return (
        <Content>
            <div className='container mx-auto py-10'>
                <div className='flex justify-between items-center mb-6'>
                    <h1 className='text-2xl font-bold'>Gutscheinkarten</h1>
                    {isAdmin && (
                        <Button onClick={() => setIsCreateDialogOpen(true)}>
                            <Plus className='mr-2 h-4 w-4' />
                            Neue Gutscheinkarte
                        </Button>
                    )}
                </div>

                {/* Filters */}
                <Card className='mb-6'>
                    <CardContent className='pt-6'>
                        <div className='flex flex-wrap gap-4'>
                            <div className='flex-1 min-w-[200px]'>
                                <Label className='text-xs text-muted-foreground mb-1 block'>Suche</Label>
                                <div className='relative'>
                                    <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                                    <Input
                                        placeholder='Kunde, Nummer, Rechnungs- oder Entwurfsnummer...'
                                        value={searchQuery}
                                        onChange={(e) => {
                                            setSearchQuery(e.target.value);
                                            setPage(0);
                                        }}
                                        className='pl-10'
                                    />
                                </div>
                            </div>
                            <div className='min-w-[200px]'>
                                <Label className='text-xs text-muted-foreground mb-1 block'>Zeitraum</Label>
                                <Popover
                                    open={dateRangeOpen}
                                    onOpenChange={(open) => {
                                        setDateRangeOpen(open);
                                        if (open) {
                                            setIsSelectingToDate(false);
                                        }
                                    }}
                                >
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant='outline'
                                            className={cn(
                                                'w-full justify-start text-left font-normal',
                                                !dateFrom && !dateTo && 'text-muted-foreground'
                                            )}
                                        >
                                            <CalendarIcon className='mr-2 h-4 w-4' />
                                            {dateFrom && dateTo ? (
                                                `${format(dateFrom, 'dd.MM.yyyy', { locale: de })} - ${format(dateTo, 'dd.MM.yyyy', { locale: de })}`
                                            ) : dateFrom ? (
                                                `${format(dateFrom, 'dd.MM.yyyy', { locale: de })} - ...`
                                            ) : (
                                                'Zeitraum wählen'
                                            )}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className='w-auto p-0' align='start'>
                                        <div className='p-3 border-b h-[52px] flex flex-col justify-center'>
                                            <p className='text-sm text-muted-foreground'>
                                                {isSelectingToDate ? 'Wähle das Enddatum' : 'Wähle das Startdatum'}
                                            </p>
                                            <p className='text-xs text-muted-foreground h-4'>
                                                {dateFrom && isSelectingToDate
                                                    ? `Start: ${format(dateFrom, 'dd. MMM yyyy', { locale: de })}`
                                                    : '\u00A0'
                                                }
                                            </p>
                                        </div>
                                        <Calendar
                                            mode='range'
                                            locale={de}
                                            selected={
                                                isSelectingToDate
                                                    ? (dateFrom ? { from: dateFrom, to: dateFrom } : undefined)
                                                    : undefined
                                            }
                                            onDayClick={(day) => {
                                                if (!isSelectingToDate) {
                                                    setDateFrom(day);
                                                    setDateTo(undefined);
                                                    setIsSelectingToDate(true);
                                                } else {
                                                    let finalFrom = dateFrom;
                                                    let finalTo = day;

                                                    if (dateFrom && day < dateFrom) {
                                                        finalFrom = day;
                                                        finalTo = dateFrom;
                                                    }

                                                    setDateFrom(finalFrom);
                                                    setDateTo(finalTo);
                                                    setIsSelectingToDate(false);
                                                    setDateRangeOpen(false);
                                                    setPage(0);
                                                }
                                            }}
                                            numberOfMonths={2}
                                            defaultMonth={dateFrom || new Date()}
                                        />
                                        {(dateFrom || dateTo) && (
                                            <div className='p-2 border-t flex justify-end'>
                                                <Button
                                                    variant='ghost'
                                                    size='sm'
                                                    onClick={() => {
                                                        setDateFrom(undefined);
                                                        setDateTo(undefined);
                                                        setIsSelectingToDate(false);
                                                        setDateRangeOpen(false);
                                                        setPage(0);
                                                    }}
                                                >
                                                    Zurücksetzen
                                                </Button>
                                            </div>
                                        )}
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <div className='min-w-[150px]'>
                                <Label className='text-xs text-muted-foreground mb-1 block'>Status</Label>
                                <Select
                                    value={statusFilter}
                                    onValueChange={(value: 'all' | 'active' | 'exhausted' | 'deleted') => {
                                        setStatusFilter(value);
                                        setPage(0);
                                    }}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder='Alle Status' />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value='all'>Alle Status</SelectItem>
                                        <SelectItem value='active'>Aktiv</SelectItem>
                                        <SelectItem value='exhausted'>Aufgebraucht</SelectItem>
                                        <SelectItem value='deleted'>Gelöscht</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Table */}
                <Card>
                    <div className='rounded-md border'>
                        <Table>
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <TableHead key={header.id}>
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    Array.from({ length: 5 }).map((_, i) => (
                                        <TableRow key={i}>
                                            {columns.map((_, j) => (
                                                <TableCell key={j}>
                                                    <Skeleton className='h-4 w-full' />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : table.getRowModel().rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className='text-center py-8 text-muted-foreground'>
                                            Keine Gutscheinkarten gefunden
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    table.getRowModel().rows.map((row) => (
                                        <>
                                            <TableRow
                                                key={row.id}
                                                className='cursor-pointer hover:bg-muted/50'
                                                onClick={() => row.toggleExpanded()}
                                            >
                                                {row.getVisibleCells().map((cell) => (
                                                    <TableCell key={cell.id}>
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                            {row.getIsExpanded() && (
                                                <TableRow key={`${row.id}-expanded`}>
                                                    <TableCell colSpan={columns.length} className='p-0'>
                                                        <ExpandedRowContent
                                                            giftCard={row.original}
                                                            isAdmin={isAdmin}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </Card>

                {/* Pagination */}
                {data && data.pageCount > 1 && (
                    <div className='flex items-center justify-between mt-4'>
                        <div className='text-sm text-muted-foreground'>
                            Seite {page + 1} von {data.pageCount} ({data.totalCount} Einträge)
                        </div>
                        <div className='flex gap-2'>
                            <Button
                                variant='outline'
                                size='sm'
                                onClick={() => setPage(Math.max(0, page - 1))}
                                disabled={page === 0}
                            >
                                Zurück
                            </Button>
                            <Button
                                variant='outline'
                                size='sm'
                                onClick={() => setPage(Math.min(data.pageCount - 1, page + 1))}
                                disabled={page >= data.pageCount - 1}
                            >
                                Weiter
                            </Button>
                        </div>
                    </div>
                )}

                {/* Create dialog */}
                {isAdmin && (
                    <CreateGiftCardDialog
                        open={isCreateDialogOpen}
                        onOpenChange={setIsCreateDialogOpen}
                    />
                )}

                {/* Edit dialog */}
                <Dialog open={!!editTarget} onOpenChange={(open) => { if (!open) setEditTarget(null); }}>
                    <DialogContent className='sm:max-w-[400px]'>
                        <DialogHeader>
                            <DialogTitle>Gutscheinkarte bearbeiten</DialogTitle>
                            <DialogDescription>
                                Name und Nummer der Gutscheinkarte ändern.
                            </DialogDescription>
                        </DialogHeader>
                        <div className='space-y-4'>
                            <div className='space-y-2'>
                                <Label>Name</Label>
                                <Input
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                />
                            </div>
                            <div className='space-y-2'>
                                <Label>Nummer</Label>
                                <Input
                                    value={editNumber}
                                    onChange={(e) => setEditNumber(e.target.value)}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant='outline' onClick={() => setEditTarget(null)}>
                                Abbrechen
                            </Button>
                            <Button
                                onClick={() => {
                                    if (editTarget) {
                                        updateGiftCardMutation.mutate({
                                            id: editTarget.id,
                                            name: editName,
                                            giftCardNumber: editNumber,
                                        });
                                    }
                                }}
                                disabled={updateGiftCardMutation.isPending || !editName.trim() || !editNumber.trim()}
                            >
                                {updateGiftCardMutation.isPending && (
                                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                )}
                                Speichern
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Content>
    );
};

export default GiftCards;
